import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Trophy, Save, RefreshCw, Gift, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeaderboardTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weights, setWeights] = useState({});
  const [defaults, setDefaults] = useState({});
  const [meta, setMeta] = useState({});
  const [monthlyPrize, setMonthlyPrize] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/leaderboard-config`, { headers: { Authorization: `Bearer ${token}` } });
      setWeights(res.data.weights || {});
      setDefaults(res.data.defaults || {});
      setMeta(res.data.activity_meta || {});
      setMonthlyPrize(res.data.monthly_prize || '');
    } catch {
      toast.error('Failed to load leaderboard config');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    Object.entries(meta).forEach(([key, m]) => {
      const grp = m.group || 'Other';
      if (!g[grp]) g[grp] = [];
      g[grp].push({ key, ...m });
    });
    return g;
  }, [meta]);

  const updateWeight = (k, v) => {
    const num = Math.max(0, Math.min(1000, parseInt(v) || 0));
    setWeights(w => ({ ...w, [k]: num }));
  };

  const resetDefaults = () => {
    if (!window.confirm('Reset all scoring weights to default values?')) return;
    setWeights({ ...defaults });
    toast.info('Defaults loaded — click Save to apply');
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/leaderboard-config`,
        { weights, monthly_prize: monthlyPrize },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      toast.success('Leaderboard scoring saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const archiveNow = async () => {
    const m = window.prompt('Archive which month? Format YYYY-MM (leave blank for last month):', '');
    try {
      const token = localStorage.getItem('token');
      const url = m ? `${API}/leaderboard/archive-now?month=${m}` : `${API}/leaderboard/archive-now`;
      const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Archive: ${JSON.stringify(res.data)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12 gap-2">
      <Loader2 className="animate-spin text-[#E63946]" size={16} />
      <span className="text-sm text-[hsl(var(--text-muted))]">Loading leaderboard config…</span>
    </div>
  );

  const GROUP_COLORS = {
    'Universal':   '#8B5CF6',
    'Sales Admin': '#10B981',
    'Marketing':   '#E63946',
    'IB Manager':  '#3B82F6',
    'Creative':    '#F59E0B',
    'Other':       '#6B7280',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-5 rounded-xl bg-gradient-to-r from-[#E63946]/15 to-transparent border border-[#E63946]/30">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#E63946]/20 border border-[#E63946]/40 flex items-center justify-center">
            <Trophy size={22} className="text-[#E63946]" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Leaderboard Scoring</h3>
            <p className="text-xs text-[hsl(var(--text-muted))]">Configure points awarded per activity. Monthly reset auto on 1st 00:01 GMT+8.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={resetDefaults} className="text-xs"><RefreshCw size={12} className="mr-1.5" />Reset Defaults</Button>
          <Button size="sm" variant="outline" onClick={archiveNow} className="text-xs">Archive Month…</Button>
          <Button size="sm" onClick={save} disabled={saving} className="text-xs bg-[#E63946] hover:bg-[#c62828]">
            {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1.5" />}Save Changes
          </Button>
        </div>
      </div>

      {/* Monthly Prize */}
      <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
        <label className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2 flex items-center gap-1.5">
          <Gift size={11} className="text-[#F59E0B]" /> Monthly Prize Message (shown on leaderboard banner)
        </label>
        <Input
          value={monthlyPrize}
          onChange={(e) => setMonthlyPrize(e.target.value)}
          placeholder="e.g. Top 5 dapat RM50 voucher Lazada — runs until end of month"
          className="h-10 text-sm bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]"
          data-testid="monthly-prize-input"
          maxLength={200}
        />
        <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1.5">Leave blank to hide banner.</p>
      </div>

      {/* Scoring Groups */}
      {Object.entries(grouped).map(([groupName, items]) => {
        const color = GROUP_COLORS[groupName] || '#6B7280';
        return (
          <div key={groupName} className="rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] overflow-hidden">
            <div className="px-5 py-3 border-b border-[hsl(var(--border))] flex items-center gap-3"
              style={{ background: `linear-gradient(90deg, ${color}18, transparent)` }}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
              <h4 className="text-sm font-bold text-white">{groupName}</h4>
              <span className="text-[10px] text-[hsl(var(--text-muted))]">{items.length} activities</span>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map(item => (
                <div key={item.key} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{item.label}</p>
                    {item.desc && <p className="text-[10px] text-[hsl(var(--text-muted))] truncate" title={item.desc}>{item.desc}</p>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-[hsl(var(--text-muted))]">pts</span>
                    <Input
                      type="number"
                      min={0}
                      max={1000}
                      value={weights[item.key] ?? 0}
                      onChange={(e) => updateWeight(item.key, e.target.value)}
                      className="h-9 w-20 text-sm font-mono text-center font-bold bg-[#111118] border-[hsl(var(--border))]"
                      data-testid={`weight-${item.key}`}
                    />
                    {weights[item.key] !== defaults[item.key] && (
                      <button onClick={() => updateWeight(item.key, defaults[item.key])} title="Reset to default"
                        className="text-[9px] text-[#F59E0B] hover:text-[#E63946]">↺</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
